/**
 * Черновик публикации на `/posts/:id`: несохранённые правки поверх ответа сервера.
 *
 * Экран показывает «сервер + свои правки», а правки уходят `PATCH` очередью. Здесь — чистые
 * правила этой склейки (что показать, что отправить, что снять после ответа), без React:
 * их легко проверить `node --test`, а ошибка в них стоит потерянного текста.
 * Статус — У КАЖДОЙ СЕТИ свой (№285), поэтому черновик живёт и у частично утверждённой публикации:
 * заморожена только утверждённая сеть (`postPlan.netFrozen`), и вкладка по умолчанию выбирается из
 * тех, что ещё правятся. Импорт — с расширением.
 */
import {
  dressCityAccounts,
  netFrozen,
  NETWORKS,
  type PostAccount,
  type PostDetail,
  type PostGen,
  type PostNetwork,
} from "./postPlan.ts";

/** Правка полей одной сети: только переданные ключи (как тело `PATCH`). Рамки здесь больше нет —
 * она переехала на элемент карусели (`postsApi.setMediaCrop`), правится отдельно от этих полей. */
export type NetEdit = Partial<{
  title: string;
  text: string;
  hashtags: string;
  alt: string;
  crm_location_id: number | null;
  city_name: string | null;
}>;

/** `accounts` — ПОЛНЫЙ набор отмеченных; нет ключа — галочки не трогали. */
export interface Edits {
  accounts?: number[];
  networks: Record<string, NetEdit>;
}

export const noEdits = (): Edits => ({ networks: {} });

export function isEmpty(e: Edits): boolean {
  return e.accounts === undefined && Object.values(e.networks).every((n) => Object.keys(n).length === 0);
}

/** `b` поверх `a`: набор аккаунтов заменяется, поля сетей сливаются. */
export function mergeEdits(a: Edits, b: Edits): Edits {
  const networks: Record<string, NetEdit> = { ...a.networks };
  for (const [code, fields] of Object.entries(b.networks)) networks[code] = { ...networks[code], ...fields };
  const out: Edits = { networks };
  const acc = b.accounts ?? a.accounts;
  if (acc !== undefined) out.accounts = acc;
  return out;
}

/** Тело `PATCH` без `base_modify`. */
export function patchBody(e: Edits): { accounts?: number[]; networks?: Record<string, NetEdit> } {
  const out: { accounts?: number[]; networks?: Record<string, NetEdit> } = {};
  if (e.accounts !== undefined) out.accounts = [...e.accounts];
  const nets = Object.entries(e.networks).filter(([, f]) => Object.keys(f).length > 0);
  if (nets.length > 0) out.networks = Object.fromEntries(nets);
  return out;
}

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

const sameSet = (a: number[], b: number[]): boolean => {
  const sb = new Set(b);
  return a.length === b.length && a.every((v) => sb.has(v));
};

/**
 * После ответа на `sent`: из `local` уходит то, что отправлено и с тех пор не менялось
 * (сервер уже держит это значение). Правленное во время запроса остаётся — уйдёт следующим `PATCH`.
 */
export function settle(local: Edits, sent: Edits): Edits {
  const out: Edits = { networks: {} };
  if (local.accounts !== undefined && !(sent.accounts !== undefined && sameSet(local.accounts, sent.accounts))) {
    out.accounts = local.accounts;
  }
  for (const [code, fields] of Object.entries(local.networks)) {
    const was = sent.networks[code] ?? {};
    const left: NetEdit = {};
    for (const [k, v] of Object.entries(fields) as [keyof NetEdit, unknown][]) {
      if (!(k in was) || !same(was[k], v)) (left as Record<string, unknown>)[k] = v;
    }
    if (Object.keys(left).length > 0) out.networks[code] = left;
  }
  return out;
}

const TEXT_FIELDS = ["title", "text", "hashtags", "alt"] as const;

/** «Правлено после генерации», как `postplan.is_edited`: NULL и пустая строка равны. */
export function isEdited(net: Pick<PostNetwork, (typeof TEXT_FIELDS)[number]>, gen: PostGen | null | undefined): boolean {
  if (!gen) return TEXT_FIELDS.some((k) => Boolean(net[k]));
  return TEXT_FIELDS.some((k) => (net[k] ?? "") !== (gen[k] ?? ""));
}

/** Что показать: ответ сервера + свои правки; `active` и `edited` пересчитаны по правкам. */
export function applyEdits(base: PostDetail, local: Edits): PostDetail {
  const set = local.accounts ? new Set(local.accounts) : null;
  const accounts = base.accounts.map((n) => ({
    ...n,
    accounts: set ? n.accounts.map((a) => ({ ...a, checked: set.has(a.id) })) : n.accounts,
  }));
  const networks: Record<string, PostNetwork> = { ...base.networks };
  for (const code of NETWORKS) {
    const net = base.networks[code];
    if (!net) continue;
    const edit = local.networks[code] ?? {};
    const merged: PostNetwork = { ...net, ...edit };
    const block = accounts.find((n) => n.code === code);
    merged.active = block ? block.accounts.some((a) => a.checked) : net.active;
    if (Object.keys(edit).length > 0) {
      merged.edited = isEdited(merged, (base.gens[code] ?? []).find((g) => g.id === net.gen_id));
    }
    networks[code] = merged;
  }
  return { ...base, accounts, networks };
}

/** Отмеченные аккаунты показа. */
export function checkedIds(d: PostDetail): number[] {
  return d.accounts.flatMap((n) => n.accounts.filter((a) => a.checked).map((a) => a.id));
}

/** Сколько аккаунтов отмечено по сети (архивные отмеченные тоже — они в публикации). */
export function netCounts(d: PostDetail): Record<string, number> {
  return Object.fromEntries(d.accounts.map((n) => [n.code, n.accounts.filter((a) => a.checked).length]));
}

/**
 * Сеть по умолчанию: первая с отмеченными аккаунтами, у которой ещё есть что делать (сеть в
 * ЧЕРНОВИКЕ), затем первая с отмеченными вовсе, иначе первая с форматом. Частично утверждённая
 * публикация (№285) открывается на недоделанной сети, а не на уже утверждённой — её всё равно
 * нельзя править.
 */
export function defaultNet(d: PostDetail): string {
  const withFormat = d.accounts.filter((n) => d.formats[n.code]);
  const counts = netCounts(d);
  const checked = withFormat.filter((n) => (counts[n.code] ?? 0) > 0);
  const open = checked.filter((n) => !netFrozen(d.networks[n.code]));
  return (open[0] ?? checked[0] ?? withFormat[0])?.code ?? "instagram";
}

/** Сеть для записи в `?net=`: null — в адресе уже годная вкладка, иначе сеть по умолчанию (фиксируем один раз). */
export function pinnedNet(d: PostDetail, wanted: string | null): string | null {
  return wanted && d.accounts.some((n) => n.code === wanted && d.formats[n.code]) ? null : defaultNet(d);
}

/** Id всех аккаунтов сети (для «все» / «города» / «снять» внутри сети). */
export function netIds(d: PostDetail, code: string): number[] {
  return (d.accounts.find((n) => n.code === code)?.accounts ?? []).map((a) => a.id);
}

/**
 * Единственный живой аккаунт сети (выбирать нечего — VK, Pinterest) или `null`,
 * если живых аккаунтов несколько (или ни одного) — тогда в сети нужен список и выбор.
 */
export function soleAccount(d: PostDetail, code: string): PostAccount | null {
  const live = (d.accounts.find((n) => n.code === code)?.accounts ?? []).filter((a) => !a.archived);
  return live.length === 1 ? live[0] ?? null : null;
}

/** «города платья»: живые аккаунты городов наличия; федеральные не входят. */
export function cityIds(d: PostDetail): number[] {
  const cities = (d.dress.cities ?? []).map((c) => c.crm_location_id);
  return dressCityAccounts(
    d.accounts.flatMap((n) => n.accounts),
    cities,
  );
}

/**
 * Правка «новый набор галочек». VK становится отмеченным, а метки места у него нет —
 * вместе уходит город ОТМЕЧЕННОГО городского аккаунта VK (не платья); отмечен федеральный
 * аккаунт (или городского аккаунта среди отмеченных нет) — метка остаётся пустой.
 */
export function accountsEdit(d: PostDetail, ids: number[]): Edits {
  const out: Edits = { accounts: ids, networks: {} };
  const vk = d.networks.vk;
  const vkAccounts = d.accounts.find((n) => n.code === "vk")?.accounts ?? [];
  const checked = vkAccounts.find((a) => a.crm_location_id !== null && ids.includes(a.id));
  if (vk && !vk.active && vk.crm_location_id === null && checked) {
    out.networks.vk = { crm_location_id: checked.crm_location_id, city_name: checked.city_name };
  }
  return out;
}
